// Oylan stand: the load grows with the child.
//
// Every task has a level 1-3 (oylanLevel in the problem JSON, set by oylan/build_content.py),
// every topic has the child's current level. A topic lesson gives tasks of the current level
// first and does not end before the level's minimum number of solved tasks. A lesson that
// ends with the topic mastered raises the topic's level for the next day, a lesson that runs
// out of tasks lowers it on the following day. The level changes at most once a day per topic.

import { todayKey } from "./session";

export const LEVELS_STORAGE_KEY = "oylan-levels";
export const MIN_LEVEL = 1;
export const MAX_LEVEL = 3;

// Level 1 keeps the plain mastery rule (3 correct in a row), higher levels add volume.
const MIN_TASKS = { 1: 3, 2: 4, 3: 5 };

export function levelOf(levels, skill, today = todayKey()) {
    const record = levels?.[skill];
    const level = record?.day < today && Number.isInteger(record?.nextLevel)
        ? record.nextLevel : record?.level;
    return Number.isInteger(level) && level >= MIN_LEVEL && level <= MAX_LEVEL ? level : MIN_LEVEL;
}

export function outcomeToday(levels, skill, today = todayKey()) {
    const record = levels?.[skill];
    return record?.day === today ? record.outcome : undefined;
}

// Earlier saves raised level immediately and stored no outcome. A mastered old
// lesson can otherwise reopen at its new level on the same day. Infer only when
// its saved mastery and solved count both support the former level's completion.
export function legacyCompletedToday(levels, skill, today, mastery, solvedCount, threshold) {
    const record = levels?.[skill];
    return record?.day === today && record.outcome == null && record.nextLevel == null &&
        Number.isInteger(record.level) && record.level > MIN_LEVEL && record.level <= MAX_LEVEL &&
        Number.isFinite(mastery) && mastery >= threshold &&
        Number.isInteger(solvedCount) && solvedCount >= minTasks(record.level - 1);
}

export function minTasks(level) {
    return MIN_TASKS[level] ?? MIN_TASKS[MIN_LEVEL];
}

// outcome: "completed" (topic mastered) raises the level, "exhausted" lowers it.
// Stores the outcome today, but applies its level change only on the next day.
// Legacy records without nextLevel already contain the applied level.
export function applyOutcome(levels, skill, outcome, today) {
    const current = levels?.[skill];
    if (current?.day === today) return levels;
    const level = levelOf(levels, skill, today);
    const next = outcome === "completed" ? Math.min(level + 1, MAX_LEVEL)
        : outcome === "exhausted" ? Math.max(level - 1, MIN_LEVEL)
        : level;
    return { ...levels, [skill]: { level, nextLevel: next, day: today, outcome } };
}

// Distance of a task from the child's level; ties go to the easier task.
export function levelRank(problemLevel, childLevel) {
    const level = Number.isInteger(problemLevel) ? problemLevel : MIN_LEVEL;
    return Math.abs(level - childLevel) * 10 + level;
}
